import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, Receipt, Loader2, Copy, Clock, CheckCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { API } from "@/lib/api";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(amount);

export const VendorInvoicePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const printRef = useRef(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await fetch(`${API}/vendor-invoices/${id}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setInvoice(data);
        // Generate QR code for UPI payment
        if (data.vendor_upi_id) {
          const qr = await QRCode.toDataURL(
            `upi://pay?pa=${data.vendor_upi_id}&pn=${encodeURIComponent(data.vendor_name)}`,
            { width: 120, margin: 1, color: { dark: "#0f172a", light: "#ffffff" } }
          );
          setQrCodeUrl(qr);
        }
      } catch {
        toast.error("Failed to load vendor invoice");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  // Auto-download if ?download=true
  useEffect(() => {
    if (invoice && searchParams.get("download") === "true") {
      handleDownloadPDF();
      navigate(`/vendor-invoice/${id}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        allowTaint: true,
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL("image/png");

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Overlay a clickable link annotation on the UPI text area
      if (invoice?.vendor_upi_id) {
        const upiEl = printRef.current.querySelector("[data-upi-link]");
        if (upiEl) {
          const containerRect = printRef.current.getBoundingClientRect();
          const elRect = upiEl.getBoundingClientRect();
          const scaleX = imgWidth / containerRect.width;
          const scaleY = imgHeight / containerRect.height;
          const lx = (elRect.left - containerRect.left) * scaleX;
          const ly = (elRect.top - containerRect.top) * scaleY;
          const lw = elRect.width * scaleX;
          const lh = elRect.height * scaleY;
          const linkPage = Math.floor(ly / pageHeight);
          const lyOnPage = ly - linkPage * pageHeight;
          pdf.setPage(linkPage + 1);
          pdf.link(lx, lyOnPage, lw, lh, { url: `upi://pay?pa=${invoice.vendor_upi_id}&pn=${encodeURIComponent(invoice.vendor_name || "")}` });
        }
      }

      pdf.save(`vendor_invoice_${invoice.invoice_number}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const copySigningLink = () => {
    if (!invoice?.signing_token) return;
    navigator.clipboard.writeText(`${window.location.origin}/sign/${invoice.signing_token}`);
    toast.success("Signing link copied!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="vendor-invoice-preview-loading">
        <div className="animate-pulse text-slate-500">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-20" data-testid="vendor-invoice-not-found">
        <p className="text-slate-500 mb-4">Invoice not found.</p>
        <Link to="/vendor-history"><Button variant="outline">Back to History</Button></Link>
      </div>
    );
  }

  const isPending = invoice.signature_status !== "Signed";

  return (
    <div className="max-w-2xl mx-auto space-y-4 px-4 sm:px-0" data-testid="vendor-invoice-preview-page">
      {/* Top bar */}
      <div className="flex items-center justify-between py-2">
        <Link to="/vendor-history">
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 -ml-2" data-testid="back-to-vendor-history">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </Link>
        <Button onClick={handleDownloadPDF} disabled={downloading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-vendor-pdf-btn">
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {downloading ? "Generating..." : "Download PDF"}
        </Button>
      </div>

      {/* Signature Status Banner */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${isPending ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-center gap-2">
          {isPending
            ? <Clock className="w-4 h-4 text-amber-600 shrink-0" />
            : <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
          }
          <span className={`text-sm font-medium ${isPending ? "text-amber-800" : "text-emerald-800"}`}>
            {isPending
              ? "Merchant Signature Pending"
              : `Signed${invoice.signature_timestamp ? " · " + format(new Date(invoice.signature_timestamp), "dd MMM yyyy, hh:mm a") : ""}`
            }
          </span>
        </div>
        {isPending && invoice.signing_token && (
          <Button variant="ghost" size="sm" className="gap-1.5 text-amber-700 hover:text-amber-800 hover:bg-amber-100" onClick={copySigningLink}>
            <Copy className="w-3.5 h-3.5" />
            Copy Signing Link
          </Button>
        )}
      </div>

      {/* Invoice Card — captured by html2canvas via printRef */}
      <Card ref={printRef} className="border-slate-200 shadow-sm bg-white" data-testid="vendor-invoice-card">
        <CardContent className="p-6 sm:p-10 space-y-6">
          {/* Vendor Header */}
          <div className="text-center pb-6 border-b-2 border-slate-200">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 mx-auto mb-3">
              <Receipt className="w-5 h-5 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 uppercase tracking-wide">{invoice.vendor_name}</h1>
            <p className="text-slate-500 text-sm mt-1">Prop: {invoice.vendor_name}</p>
            {invoice.vendor_address && <p className="text-slate-400 text-xs mt-1">{invoice.vendor_address}</p>}
            <p className="text-slate-400 text-xs mt-1">Phone: {invoice.vendor_contact}</p>
          </div>

          {/* Invoice Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Invoice No.</p>
              <p className="font-mono font-semibold text-slate-800" data-testid="invoice-number">{invoice.invoice_number}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Date</p>
              <p className="font-semibold text-slate-800">{format(new Date(invoice.invoice_date), "dd MMM yyyy")}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Billed To</p>
              <p className="font-semibold text-slate-800">Pokus AI (Pokus Technologies Pvt Ltd)</p>
              <p className="text-xs text-slate-500 mt-0.5">GSTIN: 29AAQCP4908B1Z8</p>
            </div>
            <div className="col-span-2">
              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${isPending ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {isPending
                  ? <><Clock className="w-3 h-3" /> Merchant Signature Pending</>
                  : <><CheckCircle className="w-3 h-3" /> Signed{invoice.signature_timestamp ? " · " + format(new Date(invoice.signature_timestamp), "dd MMM yyyy") : ""}</>
                }
              </span>
            </div>
          </div>

          {/* Line Items */}
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm min-w-[400px]" data-testid="line-items-table">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-2 text-xs text-slate-500 font-semibold uppercase">#</th>
                  <th className="text-left py-2 px-2 text-xs text-slate-500 font-semibold uppercase">Item / Service</th>
                  <th className="text-center py-2 px-2 text-xs text-slate-500 font-semibold uppercase">Qty</th>
                  <th className="text-right py-2 px-2 text-xs text-slate-500 font-semibold uppercase">CP</th>
                  <th className="text-right py-2 px-2 text-xs text-slate-500 font-semibold uppercase">SP</th>
                  <th className="text-right py-2 px-2 text-xs text-slate-500 font-semibold uppercase">Pokus Fee</th>
                  <th className="text-right py-2 px-2 text-xs text-slate-500 font-semibold uppercase">Vendor Amt</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items || []).map((item, i) => {
                  const cp = item.cost_price || item.price_per_unit || 0;
                  const sp = item.selling_price || item.price_per_unit || 0;
                  const vendorAmt = item.quantity * cp;
                  const fee = item.pokus_fee || (item.quantity * sp - vendorAmt);
                  return (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-3 px-2 text-slate-400">{i + 1}</td>
                      <td className="py-3 px-2 text-slate-800">{item.service_type}</td>
                      <td className="py-3 px-2 text-center font-mono text-slate-600">{item.quantity}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-600">{formatCurrency(cp)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-600">{formatCurrency(sp)}</td>
                      <td className={`py-3 px-2 text-right font-mono ${fee >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(fee)}</td>
                      <td className="py-3 px-2 text-right font-mono font-semibold text-slate-800">{formatCurrency(vendorAmt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-56 space-y-2 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Vendor Subtotal</span>
                <span className="font-mono">{formatCurrency(invoice.vendor_subtotal || invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Customer Subtotal</span>
                <span className="font-mono">{formatCurrency(invoice.customer_subtotal || invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-emerald-600">
                <span>Pokus Fees</span>
                <span className="font-mono">{formatCurrency(invoice.pokus_fees_total || 0)}</span>
              </div>
              {invoice.delivery_fee > 0 && (
                <div className="flex justify-between text-slate-500">
                  <span>Delivery Fee</span>
                  <span className="font-mono">+{formatCurrency(invoice.delivery_fee)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base pt-2 border-t-2 border-slate-200 text-slate-900">
                <span>Vendor Total</span>
                <span className="font-mono" data-testid="total-payable">{formatCurrency(invoice.vendor_total || invoice.total_payable)}</span>
              </div>
              <div className="flex justify-between font-bold text-base text-[#0047FF]">
                <span>Customer Total</span>
                <span className="font-mono">{formatCurrency(invoice.customer_total || invoice.total_payable)}</span>
              </div>
            </div>
          </div>

          {/* Signature */}
          {invoice.signature_data ? (
            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <div className="text-right">
                <p className="text-xs text-slate-400 mb-2">Authorised Signatory</p>
                <img
                  src={invoice.signature_data}
                  alt="Merchant Signature"
                  className="h-14 border-b border-slate-800 inline-block"
                  data-testid="signature-image"
                />
                <p className="text-xs font-semibold text-slate-700 mt-1">{invoice.vendor_name}</p>
                {invoice.signature_timestamp && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Signed on {format(new Date(invoice.signature_timestamp), "dd MMM yyyy, hh:mm a")}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <div className="text-right">
                <p className="text-xs text-slate-400 mb-2">Authorised Signatory</p>
                <div className="h-14 w-48 border-b border-dashed border-slate-300 inline-block" />
                <p className="text-xs text-slate-400 mt-1">Signature Pending</p>
              </div>
            </div>
          )}

          {/* UPI QR Code */}
          {invoice.vendor_upi_id && (
            <div className="pt-4 border-t border-slate-100 text-center">
              <p className="text-xs font-semibold text-slate-600 mb-2">Scan to Pay via UPI</p>
              {qrCodeUrl && (
                <img src={qrCodeUrl} alt="UPI QR Code" className="w-20 h-20 mx-auto mb-1" />
              )}
              <p className="text-xs text-slate-400" data-upi-link>UPI: {invoice.vendor_upi_id}</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400">Generated by Pokus Invoice Generator · Pokus Technologies Pvt Ltd</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
